var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import Participant from './Participant';
import { useRoom, RoomProvider } from 'hx-twilio-video';
import { Fab, styled, useTheme, CircularProgress, Typography, LinearProgress, } from '@mui/material';
import { isRecordEmpty } from 'hx-util';
import { VideoCallLayout } from 'hx-ui';
import { BsCameraVideoFill, BsCameraVideoOffFill, BsFillMicFill, BsFillMicMuteFill, } from 'react-icons/bs';
import { MdCallEnd } from 'react-icons/md';
import { grey } from '@mui/material/colors';
import { useAsyncResourceContext } from 'hx-util';
import { audioRingtone } from 'hx-assets';
import { useAudio, useInterval } from 'react-use';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { useAnalytics } from 'hx-analytics';
export var Room = function (_a) {
    var roomName = _a.roomName, onDisconnected = _a.onDisconnected, onConnected = _a.onConnected, voiceOnly = _a.voiceOnly;
    var sendCustomAnalytics = useAnalytics().sendCustomAnalytics;
    var data = useAsyncResourceContext().data;
    var navigate = useNavigate();
    var theme = useTheme();
    var useRoomValue = useRoom({ onDisconnected: onDisconnected, voiceOnly: voiceOnly });
    var room = useRoomValue.room, error = useRoomValue.error, connectRoom = useRoomValue.connectRoom, disconnectRoom = useRoomValue.disconnectRoom, localParticipant = useRoomValue.localParticipant, remoteParticipants = useRoomValue.remoteParticipants, isCameraOn = useRoomValue.isCameraOn, toggleCamera = useRoomValue.toggleCamera, isMicrophoneOn = useRoomValue.isMicrophoneOn, toggleMicrophone = useRoomValue.toggleMicrophone;
    useEffect(function () {
        if (isRecordEmpty(room) && (data === null || data === void 0 ? void 0 : data.token) && roomName) {
            connectRoom({
                token: data === null || data === void 0 ? void 0 : data.token,
                options: {
                    name: roomName,
                    dominantSpeaker: true,
                },
            });
            return function () { return disconnectRoom(); };
        }
    }, [connectRoom, disconnectRoom, room, roomName, data === null || data === void 0 ? void 0 : data.token]);
    var handleEndCallClick = function () {
        if (isRecordEmpty(room)) {
            sendCustomAnalytics({
                type: 'calls',
                message: 'call-cancelled',
            });
            navigate(-1);
        }
        disconnectRoom();
    };
    if (error)
        return (_jsx(Alert, __assign({ severity: "error" }, { children: _jsx(Typography, __assign({ variant: "body1" }, { children: error.message })) })));
    return (_jsx(RoomProvider, __assign({ room: useRoomValue }, { children: _jsx(VideoCallLayout, { localParticipant: _jsx(Participant, { flipVideo: true, voiceOnly: voiceOnly, className: "border-2 rounded-md border-[#999]", participant: localParticipant, fallback: _jsx("div", __assign({ className: "grid w-full h-full place-items-center", style: { backgroundColor: grey[900] } }, { children: _jsx(CircularProgress, {}) })) }), remoteParticipants: _jsx(_Fragment, { children: remoteParticipants.length === 0 ? (_jsx(WaitingForAgent, {})) : (remoteParticipants.map(function (participant, index) { return (_jsx(Participant, { voiceOnly: voiceOnly, participant: participant, fallback: _jsx("div", __assign({ className: "grid w-full h-full place-items-center", style: { backgroundColor: grey[900] } }, { children: _jsx(CircularProgress, {}) })) }, "remote-participant-".concat(index))); })) }), controls: _jsxs("div", __assign({ className: "flex gap-8" }, { children: [_jsx(ControlsButton, __assign({ onClick: function () { return toggleMicrophone(); } }, { children: isMicrophoneOn ? (_jsx(BsFillMicFill, { color: theme.palette.common.white })) : (_jsx(BsFillMicMuteFill, { color: theme.palette.common.white })) })), _jsx(ControlsButton, __assign({ color: theme.palette.error.main, onClick: handleEndCallClick }, { children: _jsx(MdCallEnd, { color: theme.palette.common.white }) })), _jsx(ControlsButton, __assign({ onClick: function () { return toggleCamera(); }, disabled: voiceOnly }, { children: isCameraOn ? (_jsx(BsCameraVideoFill, { color: theme.palette.common.white })) : (_jsx(BsCameraVideoOffFill, { color: theme.palette.common.white })) }))] })) }) })));
};
export default Room;
var ControlsButton = styled(Fab, {
    shouldForwardProp: function (prop) { return 'color' !== prop; },
})(function (_a) {
    var theme = _a.theme, color = _a.color;
    return ({
        backgroundColor: color || theme.palette.common.black,
    });
});
var WaitingForAgent = function () {
    var _a = useState(0), waitingTime = _a[0], setWaitingTime = _a[1];
    var _b = useAudio({
        src: audioRingtone,
        loop: true,
    }), ringtoneAudio = _b[0], _c = _b[2], play = _c.play, pause = _c.pause, volume = _c.volume;
    useEffect(function () {
        volume(0.3);
        play();
        return function () { return pause(); };
    }, []);
    useInterval(function () {
        setWaitingTime(function (time) { return time + 1; });
    }, 1000);
    return (_jsxs(_Fragment, { children: [ringtoneAudio, _jsxs("div", __assign({ className: "grid w-full h-full gap-4 bg-gray-50 place-content-center" }, { children: [_jsx(Typography, __assign({ variant: "body2" }, { children: "Waiting for the next available agent." })), _jsx(LinearProgress, { variant: "indeterminate" }), _jsxs(Typography, __assign({ variant: "body1" }, { children: ["Your current waiting time is ", waitingTime, "."] }))] }))] }));
};
